import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Megaphone, Square, Clock, Building } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { formatDateTime } from '@/lib/formatters';
import { 
  getAllAnnouncements, 
  stopAnnouncement,
  type Announcement 
} from '@/services/announcementService';

export const AnnouncementManagement = () => {
  const { toast } = useToast();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAnnouncements = async () => {
    try {
      const data = await getAllAnnouncements();
      setAnnouncements(data);
    } catch (error) {
      console.error('Error fetching announcements:', error);
      toast({
        title: "Error",
        description: "Failed to load announcements",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const handleStop = async (announcementId: string) => {
    try {
      await stopAnnouncement(announcementId);
      
      toast({
        title: "Success",
        description: "Announcement stopped successfully",
      });
      
      fetchAnnouncements();
    } catch (error: any) {
      toast({
        title: "Error", 
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const activeAnnouncements = announcements.filter(a => 
    a.is_active && new Date(a.end_time) > new Date()
  );

  if (loading) {
    return (
      <Card>
        <CardHeader style={{ backgroundColor: 'orange' }}>
          <CardTitle className="flex items-center gap-2">
            <Megaphone className="h-5 w-5" />
            Announcement Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center p-8">
            <div className="text-muted-foreground">Loading announcements...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader style={{ backgroundColor: 'orange' }}>
        <CardTitle className="flex items-center gap-2">
          <Megaphone className="h-5 w-5" />
          Announcement Management
        </CardTitle>
        <CardDescription>
          Manage announcements from all organizations
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Active Count Alert */}
        {activeAnnouncements.length > 0 && (
          <Alert>
            <Megaphone className="h-4 w-4" />
            <AlertDescription>
              There are {activeAnnouncements.length} active announcements across all organizations.
            </AlertDescription>
          </Alert>
        )}

        {/* Announcements List */}
        <div className="space-y-3">
          <h3 className="font-medium">All Announcements</h3>
          
          {announcements.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No announcements found.
            </div>
          ) : (
            <div className="space-y-3">
              {announcements.map((announcement) => {
                const isActive = announcement.is_active && new Date(announcement.end_time) > new Date();
                const isExpired = new Date(announcement.end_time) <= new Date();
                
                return (
                  <div key={announcement.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <Badge variant={isActive ? "default" : "secondary"}>
                            {isActive ? "Active" : isExpired ? "Expired" : "Stopped"}
                          </Badge>
                          
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Building className="h-3 w-3" />
                            {announcement.organization?.name || 'Unknown Organization'}
                          </div>
                          
                          <div className="text-sm text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Created: {formatDateTime(announcement.created_at)}
                          </div>
                        </div>
                        
                        <p className="text-sm mb-2">{announcement.comments}</p>
                        
                        <div className="text-xs text-muted-foreground">
                          Expires: {formatDateTime(announcement.end_time)}
                        </div>
                      </div>
                      
                      {isActive && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleStop(announcement.id)}
                        >
                          <Square className="h-3 w-3 mr-1" />
                          Stop
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};